'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Eye,
  FileText,
  History,
  Loader2,
  Search,
  Sparkles
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ocrTemplateQueries,
  TemplateFieldsInfo,
  type OcrTemplate
} from '@/features/ocr-templates';
import {
  MoveToRepairDialog,
  type MoveToRepairPayload
} from '@/features/repair-settings';
import { PermissionGate } from '@/features/auth';
import {
  useRefuseOcrContainerMutation,
  useRegeneratePoolSuggestionsMutation,
  useStartOcrMutation,
  useSyncRegistrationOcrTemplatesMutation
} from '../services/ocr.mutations';
import {
  ocrQueries,
  resetSuggestionPollWindow,
  suggestionPollingTimedOut
} from '../services/ocr.queries';
import { useTemplateSuggestionsLive } from '../hooks/use-template-suggestions-live';
import { useOcrToast } from '../utils/ocr-toast';
import { OCR_TEMPLATE_PICKER_MAX } from '../constants';
import {
  capTemplateSelection,
  rankedSuggestionIds,
  seedTemplateSelection,
  toggleTemplateSelection
} from '../utils/template-selection';
import { OcrPerPageAssignment } from './ocr-per-page-assignment';
import { SuggestionHistoryPanel } from './suggestion-history-panel';
import type { OcrSuggestionRunDetail } from '../types';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { API_ENDPOINTS } from '@/lib/api/config';
import { BatchPdfViewer } from '@/features/photo-scanning';

interface OcrTemplatePoolDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  containerId: number;
  registrationId: number;
  containerLabel?: string;
  /**
   * The container already has a run: the "Assign per page" tab re-runs only
   * the pages the operator ticks (OMA-674). The pool tab still starts over.
   */
  rerunExistingRun?: boolean;
  /**
   * Latest scan batch id — enables the document preview. The PDF is streamed
   * through the authenticated batch endpoint, so no signed URL is needed.
   */
  batchId?: number | null;
}

type StartTab = 'pool' | 'perPage';

// Temporary UI-only limit — see OCR_TEMPLATE_PICKER_MAX.
const SINGLE_SELECT = OCR_TEMPLATE_PICKER_MAX === 1;

export function OcrTemplatePoolDialog({
  open,
  onOpenChange,
  containerId,
  registrationId,
  containerLabel,
  rerunExistingRun = false,
  batchId
}: OcrTemplatePoolDialogProps) {
  const t = useTranslations('ocr');
  const tRepair = useTranslations('repairs');
  const toasts = useOcrToast();
  const [tab, setTab] = useState<StartTab>('perPage');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [hasRequestedSuggestions, setHasRequestedSuggestions] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [poolHistoryOpen, setPoolHistoryOpen] = useState(false);
  // Nested inside this dialog, so it stays on local state rather than the URL
  // hash (a hash-wired overlay would clobber the parent's own key).
  const [refuseOpen, setRefuseOpen] = useState(false);
  const pdfBlobUrl = useAuthenticatedBlobUrl(
    batchId != null ? API_ENDPOINTS.PHOTO_SCANNING.BATCH_PDF(batchId) : null
  );
  const canPreview = batchId != null;

  // The container's configured default template (set at creation for test
  // containers, or via the registration's form). Shares the cache with the
  // "Assign per page" tab's pages query, so this adds no extra request.
  const defaultTemplateQuery = useQuery(
    ocrQueries.containerPages(containerId, open)
  );
  const defaultTemplate = defaultTemplateQuery.data?.data?.default_template;
  const defaultTemplateId = defaultTemplate?.id ?? null;
  // Pre-tick the default in the pool list once per dialog open — re-armed on
  // close so each open starts from the default again.
  const seededDefaultRef = useRef(false);

  const regeneratePoolMutation = useRegeneratePoolSuggestionsMutation();

  // Apply a past pool run from history — prefer the chosen template ids, else
  // its suggested ones.
  const applyPoolRun = (run: OcrSuggestionRunDetail) => {
    if (run.type !== 'pool') return;
    const chosen = Array.isArray(run.assignment) ? run.assignment : null;
    const suggested = Array.isArray(run.suggestions)
      ? run.suggestions.map((entry) => entry.template_id)
      : null;
    setSelected(capTemplateSelection(chosen ?? suggested ?? []));
    // Keep the popover open so the ‹ prev/next › pager stays usable while the
    // operator compares runs; they close it themselves once satisfied.
  };

  const handlePoolRegenerate = () => {
    resetSuggestionPollWindow(registrationId);
    setHasRequestedSuggestions(true);
    regeneratePoolMutation.mutate(registrationId);
    setPoolHistoryOpen(false);
  };

  const suggestionsQuery = useQuery(
    ocrQueries.templateSuggestions(registrationId, hasRequestedSuggestions)
  );
  // Push-first: the backend job broadcasts the computed suggestions on a
  // registration-scoped channel; this patches the query cache instantly
  // and the query's poll loop degrades to a slow safety net.
  useTemplateSuggestionsLive(registrationId, open && hasRequestedSuggestions);
  // Infinite scroll: pages load on demand as the list scrolls, so huge
  // template catalogs stay cheap to open.
  const templatesQuery = useInfiniteQuery(
    ocrTemplateQueries.infiniteList({ per_page: 25, is_active: true, search })
  );
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const startMutation = useStartOcrMutation();
  const syncMutation = useSyncRegistrationOcrTemplatesMutation();
  const refuseMutation = useRefuseOcrContainerMutation();

  const templates = useMemo(
    () =>
      (templatesQuery.data?.pages.flatMap((page) => page.data ?? []) ??
        []) as OcrTemplate[],
    [templatesQuery.data]
  );

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = templatesQuery;
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !open) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    });
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);
  const suggestionsPayload = suggestionsQuery.data?.data;
  const suggestions = useMemo(
    () => suggestionsPayload?.suggestions ?? [],
    [suggestionsPayload]
  );
  // The poll loop gave up while the backend still says 'pending' (e.g. a
  // dead queue worker) — fall through to the "unavailable" copy instead of
  // spinning forever.
  const pollingTimedOut =
    suggestionsPayload?.status === 'pending' &&
    !suggestionsQuery.isFetching &&
    suggestionPollingTimedOut(registrationId);
  // 'pending' = the backend suggestion job is still computing; the query
  // polls until it flips to 'ready'. The manual list below stays fully
  // usable while this spins.
  const isAnalyzing =
    !pollingTimedOut &&
    (suggestionsQuery.isFetching || suggestionsPayload?.status === 'pending');

  // Once a suggestion list lands, pre-select its best match(es) up to the
  // picker limit — the full list stays visible to pick another. Keyed by the
  // suggested ids so the poll's safety-net refetches don't undo a manual pick.
  const appliedSuggestionsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !hasRequestedSuggestions) {
      appliedSuggestionsRef.current = null;
      return;
    }
    if (suggestionsPayload?.status === 'pending' || suggestions.length === 0) {
      return;
    }
    const ranked = rankedSuggestionIds(suggestions);
    const key = ranked.join(',');
    if (appliedSuggestionsRef.current === key) return;
    appliedSuggestionsRef.current = key;
    setSelected(capTemplateSelection(ranked));
  }, [open, hasRequestedSuggestions, suggestionsPayload, suggestions]);

  // Reset transient state when the dialog closes so re-opens start fresh.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSearch('');
      setSelected([]);
      setHasRequestedSuggestions(false);
      setTab('perPage');
    }
    onOpenChange(next);
  };

  // Pre-select the container's default template in the pool list when the
  // dialog opens, so the pick-templates tab starts from the same default as
  // "Assign per page". Seeds once per open and yields to manual edits after.
  useEffect(() => {
    if (!open) {
      seededDefaultRef.current = false;
      return;
    }
    if (defaultTemplateId != null && !seededDefaultRef.current) {
      seededDefaultRef.current = true;
      setSelected((prev) => seedTemplateSelection(prev, defaultTemplateId));
    }
  }, [open, defaultTemplateId]);

  const toggleTemplate = (templateId: number) => {
    setSelected((prev) => toggleTemplateSelection(prev, templateId));
  };

  // Name for the single-select confirm label; the picked template may be in
  // the suggestions, the loaded page of the list, or be the default.
  const selectedTemplateName =
    selected.length === 1
      ? (suggestions.find((entry) => entry.template_id === selected[0])?.name ??
        templates.find((template) => template.id === selected[0])?.name ??
        (defaultTemplate?.id === selected[0] ? defaultTemplate.name : null))
      : null;

  const handleConfirm = async () => {
    if (selected.length === 0) return;

    try {
      // Persist the pool first so subsequent OCR runs on this registration
      // pick up the same candidates by default. Then dispatch the start with
      // the explicit ids — keeps the run reproducible even if someone edits
      // the pool right after.
      await syncMutation.mutateAsync({
        registrationId,
        templateIds: selected
      });
      await startMutation.mutateAsync({
        containerId,
        ocrTemplateIds: selected
      });
      toasts.started(containerId, containerLabel);
      handleOpenChange(false);
    } catch {
      // handled by global MutationCache
    }
  };

  // OMA-589: routing a container to the repair/processing queue is available
  // straight from this dialog and does NOT require a template — an operator who
  // opens the picker only to find the document has nothing to run against can
  // refuse it here instead of backing out into the container page.
  const handleConfirmRefuse = (data: MoveToRepairPayload) => {
    refuseMutation.mutate(
      { containerId, ...data },
      {
        onSuccess: () => {
          setRefuseOpen(false);
          handleOpenChange(false);
        }
      }
    );
  };

  const isPending = syncMutation.isPending || startMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[80vw]'>
        <DialogHeader>
          <div className='flex items-start justify-between gap-3'>
            <div className='space-y-1.5'>
              <DialogTitle>{t('startOcrTitle')}</DialogTitle>
              <DialogDescription>{t('startOcrDescription')}</DialogDescription>
            </div>
            <PermissionGate permissions={['ocr.edit']}>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='me-6 shrink-0 border-amber-500/40 text-amber-600 hover:text-amber-700'
                onClick={() => setRefuseOpen(true)}
                disabled={isPending || refuseMutation.isPending}
              >
                <AlertTriangle className='me-2 h-4 w-4' />
                {tRepair('moveToRepair')}
              </Button>
            </PermissionGate>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(value) => setTab(value as StartTab)}>
          <TabsList className='grid w-full grid-cols-2'>
            <TabsTrigger value='perPage'>{t('assignPerPage')}</TabsTrigger>
            <TabsTrigger value='pool'>
              {SINGLE_SELECT ? t('pickOneTemplate') : t('pickTemplates')}
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value='pool'
            forceMount
            className='space-y-4 data-[state=inactive]:hidden'
          >
            <div className='space-y-5 px-1 py-2'>
              {/* Document preview — same robust viewer as the listing. */}
              {canPreview ? (
                <div className='flex justify-end'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setPreviewOpen(true)}
                  >
                    <Eye className='me-2 h-4 w-4' />
                    {t('previewDocument')}
                  </Button>
                </div>
              ) : null}

              {/* AI Suggestions */}
              <section className='space-y-2'>
                <div className='flex items-center justify-between gap-2'>
                  <div className='flex items-center gap-2 text-sm font-medium'>
                    <Sparkles className='h-4 w-4 text-amber-500' />
                    {t('aiSuggestions')}
                  </div>
                  <Popover
                    open={poolHistoryOpen}
                    onOpenChange={setPoolHistoryOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button type='button' variant='outline' size='sm'>
                        <History className='me-2 h-4 w-4' />
                        {t('historyTitle')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-[360px] p-3' align='end'>
                      <SuggestionHistoryPanel
                        registrationId={registrationId}
                        filterType='pool'
                        mode='interactive'
                        enabled={poolHistoryOpen}
                        onUse={applyPoolRun}
                        onNew={handlePoolRegenerate}
                        regenerating={regeneratePoolMutation.isPending}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {!hasRequestedSuggestions ? (
                  <div className='flex flex-col items-start gap-2'>
                    <p className='text-muted-foreground text-xs'>
                      {t('getAiSuggestionsHint')}
                    </p>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        resetSuggestionPollWindow(registrationId);
                        setHasRequestedSuggestions(true);
                      }}
                      disabled={isPending}
                    >
                      <Sparkles className='me-2 h-4 w-4 text-amber-500' />
                      {t('getAiSuggestions')}
                    </Button>
                  </div>
                ) : isAnalyzing ? (
                  <div className='text-muted-foreground flex items-center gap-2 text-sm'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    {t('analyzingDocument')}
                  </div>
                ) : suggestions.length === 0 ? (
                  <p className='text-muted-foreground text-xs'>
                    {t('suggestionUnavailable')}
                  </p>
                ) : (
                  <div className='grid gap-2 sm:grid-cols-3'>
                    {suggestions.map((suggestion) => {
                      const isSelected = selected.includes(
                        suggestion.template_id
                      );
                      return (
                        <button
                          key={suggestion.template_id}
                          type='button'
                          onClick={() => toggleTemplate(suggestion.template_id)}
                          className={cn(
                            'group flex flex-col items-start gap-2 rounded-lg border p-3 text-start transition',
                            'hover:border-primary',
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-border'
                          )}
                        >
                          <div className='flex w-full items-start justify-between gap-2'>
                            <p className='text-sm leading-tight font-medium'>
                              {suggestion.name}
                            </p>
                            <span className='flex shrink-0 items-center gap-1'>
                              <TemplateFieldsInfo
                                templateId={suggestion.template_id}
                              />
                              <SimilarityRing value={suggestion.similarity} />
                            </span>
                          </div>
                          {suggestion.sample_text_excerpt ? (
                            <p className='text-muted-foreground line-clamp-2 text-xs'>
                              {suggestion.sample_text_excerpt}
                            </p>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* All templates with search */}
              <section className='space-y-2'>
                <div className='flex items-center justify-between gap-2'>
                  <div className='text-sm font-medium'>{t('allTemplates')}</div>
                  {SINGLE_SELECT ? null : (
                    <Badge variant='outline'>
                      {t('selectedCount', { count: selected.length })}
                    </Badge>
                  )}
                </div>
                <div className='relative'>
                  <Search className='text-muted-foreground absolute start-2 top-1/2 h-4 w-4 -translate-y-1/2' />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t('searchTemplates')}
                    className='ps-8'
                  />
                </div>

                <ScrollArea className='h-56 rounded-md border'>
                  {templatesQuery.isPending ? (
                    <div className='text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm'>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      {t('loadingTemplates')}
                    </div>
                  ) : templates.length === 0 ? (
                    <p className='text-muted-foreground py-6 text-center text-sm'>
                      {t('noTemplates')}
                    </p>
                  ) : (
                    <TemplateListGroup
                      single={SINGLE_SELECT}
                      value={selected[0]}
                    >
                      {templates.map((template) => {
                        const isSelected = selected.includes(template.id);
                        return (
                          <li key={template.id}>
                            <label
                              className={cn(
                                'flex cursor-pointer items-start gap-3 px-3 py-2',
                                'hover:bg-muted/50',
                                isSelected && 'bg-primary/5'
                              )}
                            >
                              {SINGLE_SELECT ? (
                                // Clicks, not onValueChange, drive it so the
                                // selected radio can be clicked again to clear.
                                <RadioGroupItem
                                  value={String(template.id)}
                                  onClick={() => toggleTemplate(template.id)}
                                  className='mt-1'
                                />
                              ) : (
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() =>
                                    toggleTemplate(template.id)
                                  }
                                  className='mt-1'
                                />
                              )}
                              <div className='flex-1'>
                                <div className='flex items-center gap-1.5'>
                                  <p className='text-sm font-medium'>
                                    {template.name}
                                  </p>
                                  <TemplateFieldsInfo
                                    templateId={template.id}
                                  />
                                </div>
                                {template.description ? (
                                  <p className='text-muted-foreground line-clamp-1 text-xs'>
                                    {template.description}
                                  </p>
                                ) : null}
                              </div>
                            </label>
                          </li>
                        );
                      })}
                    </TemplateListGroup>
                  )}
                  {/* Infinite-scroll sentinel: observed by the IntersectionObserver
                      above; entering the viewport pulls the next page. */}
                  <div ref={loadMoreRef} className='h-px' />
                  {isFetchingNextPage ? (
                    <div className='text-muted-foreground flex items-center justify-center gap-2 py-2 text-xs'>
                      <Loader2 className='h-3.5 w-3.5 animate-spin' />
                      {t('loadingTemplates')}
                    </div>
                  ) : null}
                </ScrollArea>
              </section>
            </div>

            <div className='flex justify-end gap-2'>
              <Button
                variant='outline'
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={selected.length === 0 || isPending}
              >
                {isPending && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
                {SINGLE_SELECT
                  ? selectedTemplateName
                    ? t('runWithTemplate', { name: selectedTemplateName })
                    : t('runOcr')
                  : t('runWithNTemplates', { count: selected.length })}
              </Button>
            </div>
          </TabsContent>

          <TabsContent
            value='perPage'
            forceMount
            className='data-[state=inactive]:hidden'
          >
            <OcrPerPageAssignment
              containerId={containerId}
              registrationId={registrationId}
              containerLabel={containerLabel}
              rerunExistingRun={rerunExistingRun}
              onStarted={() => handleOpenChange(false)}
            />
          </TabsContent>
        </Tabs>

        {/* Whole-document preview (Pick OCR Templates tab) — desktop renders
            the PDF inline, mobile falls back to the scanned page images. */}
        {canPreview && batchId != null ? (
          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent className='flex h-[96dvh] max-h-[96dvh] w-[98vw] sm:max-w-[110rem] flex-col gap-0 p-0'>
              <DialogHeader className='border-b px-6 py-4'>
                <DialogTitle className='flex items-center gap-2 font-mono'>
                  <FileText className='h-4 w-4' />
                  {t('viewPdf')}
                  {containerLabel ? ` — ${containerLabel}` : ''}
                </DialogTitle>
                <DialogDescription className='sr-only'>
                  {t('startOcrDescription')}
                </DialogDescription>
              </DialogHeader>
              <BatchPdfViewer
                batchId={batchId}
                pdfBlobUrl={pdfBlobUrl}
                title={containerLabel ?? t('viewPdf')}
              />
            </DialogContent>
          </Dialog>
        ) : null}

        {/* Send to processing (repair) management — no template required. */}
        <MoveToRepairDialog
          open={refuseOpen}
          onOpenChange={setRefuseOpen}
          containerNumber={containerLabel}
          onConfirm={handleConfirmRefuse}
          isPending={refuseMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * The template list's container: a radio group while the picker is
 * single-select (OCR_TEMPLATE_PICKER_MAX = 1), a plain list otherwise.
 */
function TemplateListGroup({
  single,
  value,
  children
}: {
  single: boolean;
  value: number | undefined;
  children: ReactNode;
}) {
  if (!single) return <ul className='divide-y'>{children}</ul>;
  return (
    <RadioGroup
      asChild
      value={value != null ? String(value) : ''}
      className='gap-0'
    >
      <ul className='divide-y'>{children}</ul>
    </RadioGroup>
  );
}

function SimilarityRing({ value }: { value: number }) {
  const percent = Math.max(0, Math.min(100, Math.round(value * 100)));
  const tone =
    percent >= 75
      ? 'text-emerald-500'
      : percent >= 50
        ? 'text-amber-500'
        : 'text-muted-foreground';

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full border px-1.5 text-[10px] font-semibold',
        tone
      )}
      aria-label={`${percent}% similarity`}
    >
      {percent}%
    </span>
  );
}
